// Structure de l’application : navigation latérale (ordinateur), barre haute et barre basse (téléphone),
// indicateur d’état honnête et numéro de version toujours visibles.
import { versionLabel, VERSION } from '../core/version';
import type { Store, Sync } from '../data/store';
import { esc, fmtClock, fmtShort } from './dom';
import { brandMark, icon } from './icons';

export type View = 'contacts' | 'rdv' | 'loge' | 'settings';

export interface StatusInfo {
  tone: 'ok' | 'busy' | 'warn' | 'bad' | 'off';
  text: string;
  detail: string;
}

export function statusInfo(sync: Sync, dev: boolean): StatusInfo {
  if (dev) return { tone: 'warn', text: 'Mode développement', detail: 'en mémoire · rien n’est enregistré' };
  switch (sync.phase) {
    case 'boot': return { tone: 'busy', text: 'Connexion…', detail: '' };
    case 'signed-out': return { tone: 'off', text: 'Déconnecté', detail: '' };
    case 'loading': return { tone: 'busy', text: 'Chargement de vos contacts…', detail: 'lecture du serveur' };
    case 'offline': return { tone: 'warn', text: 'Hors ligne · lecture seule', detail: sync.readAt ? (sync.fromCache ? 'copie locale du ' : 'données lues le ') + fmtShort(sync.readAt) : 'aucune copie locale' };
    case 'error': return { tone: 'bad', text: 'Erreur de synchronisation', detail: sync.message || 'cause inconnue' };
    case 'synced':
      if (sync.pending > 0) return { tone: 'busy', text: 'Enregistrement…', detail: `${sync.pending} écriture${sync.pending > 1 ? 's' : ''} en cours` };
      return {
        tone: 'ok',
        text: 'Synchronisé',
        detail: (sync.live === 'live' ? 'en direct · ' : sync.live === 'down' ? 'temps réel interrompu · ' : 'temps réel en attente · ') + 'lu à ' + (sync.readAt ? fmtClock(sync.readAt) : '—'),
      };
  }
}

export function statusPill(sync: Sync, dev: boolean): string {
  const s = statusInfo(sync, dev);
  return `<span class="status" data-tone="${s.tone}" title="${esc(s.detail)}"><i></i><span>${esc(s.text)}${s.detail ? `<small>${esc(s.detail)}</small>` : ''}</span></span>`;
}

/** Bandeau d’état au-dessus du contenu : erreur avec sa cause et Réessayer, hors ligne, copie locale. */
export function syncBanner(store: Store): string {
  const { sync } = store.state;
  if (sync.phase === 'error') {
    return `<div class="banner bad" role="alert">${icon('alert')}<div><b>Impossible de lire vos données sur le serveur.</b><br>${esc(sync.message)}${sync.fromCache && sync.readAt ? `<br>Affichage de la copie locale du ${esc(fmtShort(sync.readAt))}, en lecture seule.` : ''}</div><button class="btn small" type="button" data-retry>${icon('refresh')}Réessayer</button></div>`;
  }
  if (sync.phase === 'offline') {
    return `<div class="banner warn" role="status">${icon('offline')}<div><b>Hors ligne.</b> ${sync.readAt ? `Vous voyez ${sync.fromCache ? 'la copie locale du ' : 'les données lues le '}${esc(fmtShort(sync.readAt))}.` : 'Aucune copie locale sur cet appareil.'} Les modifications sont désactivées jusqu’au retour de la connexion.</div><button class="btn small" type="button" data-retry>${icon('refresh')}Réessayer</button></div>`;
  }
  return '';
}

export interface NavCounts { contacts: number; rdv: number }

function navItems(view: View, counts: NavCounts): string {
  const item = (v: View, label: string, ic: string, badge?: number) =>
    `<button class="nav-item" type="button" data-view="${v}" ${view === v ? 'aria-current="page"' : ''}>${icon(ic)}<span>${esc(label)}</span>${badge !== undefined ? `<span class="badge" aria-hidden="true">${badge}</span>` : ''}</button>`;
  return item('contacts', 'Contacts', 'users', counts.contacts) + item('rdv', 'Rendez-vous', 'calendar', counts.rdv) + item('loge', 'Loges', 'building') + item('settings', 'Préférences', 'settings');
}

export function renderShell(root: HTMLElement, store: Store, view: View, counts: NavCounts): HTMLElement {
  const { sync, dev } = store.state;
  root.innerHTML = `${dev ? '<div class="dev-banner">Mode développement · en mémoire · rien n’est enregistré</div>' : ''}
  <div class="layout">
    <aside class="sidebar" aria-label="Navigation principale">
      <div class="brand-lockup">${brandMark()}<span class="brand-name">ACTE·5<small>WORLD</small></span></div>
      ${navItems(view, counts)}
      <div class="nav-spacer"></div>
      <div class="side-foot"><span class="js-status">${statusPill(sync, dev)}</span><span class="version" title="Construit le ${esc(VERSION.builtAt)}">${esc(versionLabel())}</span></div>
    </aside>
    <div>
      <header class="topbar">
        <div class="brand-lockup">${brandMark()}<span class="brand-name">ACTE·5<small>WORLD</small></span></div>
        <span class="js-status">${statusPill(sync, dev)}</span>
      </header>
      <main class="main" id="main"></main>
    </div>
  </div>
  <nav class="bottom-nav" aria-label="Navigation">${navItems(view, counts)}</nav>`;
  return root.querySelector<HTMLElement>('#main')!;
}

export function updateShell(root: HTMLElement, store: Store, view: View, counts: NavCounts): void {
  const { sync, dev } = store.state;
  root.querySelectorAll<HTMLElement>('.js-status').forEach((s) => { s.innerHTML = statusPill(sync, dev); });
  root.querySelectorAll<HTMLElement>('[data-view]').forEach((b) => {
    if (b.dataset.view === view) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current');
    const badge = b.querySelector('.badge');
    if (badge && b.dataset.view === 'contacts') badge.textContent = String(counts.contacts);
    if (badge && b.dataset.view === 'rdv') badge.textContent = String(counts.rdv);
  });
}
